import { useEffect, useState } from "react";
import adminApi from "../lib/adminApi";
import AnalyticsMap from "../components/AnalyticsMap";

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);

  const reload = async (d) => {
    const dd = d ?? days;
    const [s, r] = await Promise.all([
      adminApi.get(`/analytics/summary?days=${dd}`),
      adminApi.get("/analytics/recent?limit=50"),
    ]);
    setSummary(s.data);
    setRecent(r.data.items || []);
  };

  useEffect(() => {
    reload().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const max = Math.max(1, ...((summary?.by_day || []).map((d) => d.pageviews)));

  return (
    <div data-testid="admin-analytics-page">
      <div className="label-mono">§ 04 · Analytics</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-medium mt-2">Audience &amp; trafic</h1>
      <p className="text-[var(--muted)] mt-3 max-w-2xl">
        Sessions anonymisées, pages vues, top contenus et sources de trafic.
      </p>

      <div className="mt-8 flex gap-2">
        {[7, 30, 90, 365].map((d) => (
          <button
            key={d}
            onClick={() => {
              setDays(d);
              reload(d);
            }}
            data-testid={`analytics-range-${d}`}
            className={`label-mono px-4 py-2 border-2 ${days === d ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "border-black"}`}
          >
            {d}j
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black mt-8 border border-black">
        <Stat label="Pages vues" value={summary?.total_pageviews ?? 0} testid="stat-pv" />
        <Stat label="Sessions uniques" value={summary?.total_sessions ?? 0} testid="stat-sessions" />
      </div>

      {/* Bar chart */}
      <section className="mt-10">
        <h2 className="font-serif text-2xl font-medium mb-4">Évolution quotidienne</h2>
        <div className="border border-black bg-[var(--paper)] p-6">
          {summary?.by_day?.length ? (
            <div className="flex items-end gap-1 h-48" data-testid="analytics-chart">
              {summary.by_day.map((d, i) => (
                <div key={i} className="flex-1 group relative">
                  <div
                    className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] w-full"
                    style={{ height: `${(d.pageviews / max) * 100}%` }}
                    title={`${d.date} · ${d.pageviews} vues · ${d.sessions} sessions`}
                  />
                  <div className="text-[9px] font-mono text-[var(--muted)] mt-1 text-center rotate-45 origin-top-left whitespace-nowrap">
                    {d.date?.slice(5)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-[var(--muted)] italic py-8">Pas encore de données</div>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
        <section>
          <h2 className="font-serif text-2xl font-medium mb-4">Top pages</h2>
          <div className="border border-black">
            {summary?.top_pages?.length ? (
              <table className="w-full text-sm">
                <tbody>
                  {summary.top_pages.map((p, i) => (
                    <tr key={i} className="border-b border-black/10 last:border-0">
                      <td className="p-3 font-mono text-xs break-all">{p.path}</td>
                      <td className="p-3 text-right font-mono tabular-nums">{p.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-[var(--muted)] italic text-sm">—</div>
            )}
          </div>
        </section>
        <section>
          <h2 className="font-serif text-2xl font-medium mb-4">Top referrers</h2>
          <div className="border border-black">
            {summary?.top_referrers?.length ? (
              <table className="w-full text-sm">
                <tbody>
                  {summary.top_referrers.map((p, i) => (
                    <tr key={i} className="border-b border-black/10 last:border-0">
                      <td className="p-3 font-mono text-xs">{p.host}</td>
                      <td className="p-3 text-right font-mono tabular-nums">{p.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-[var(--muted)] italic text-sm">Trafic direct uniquement.</div>
            )}
          </div>
        </section>
      </div>

      <section className="mt-10" data-testid="analytics-countries-section">
        <h2 className="font-serif text-2xl font-medium mb-4">Audience par pays</h2>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <AnalyticsMap countries={summary?.top_countries || []} focus="africa" />
          </div>
          <div className="lg:col-span-2">
            <div className="border border-black">
              {summary?.top_countries?.length ? (
                <table className="w-full text-sm">
                  <thead className="bg-[var(--ink)] text-[var(--paper)]">
                    <tr>
                      <th className="text-left p-3 label-mono w-16">Code</th>
                      <th className="text-left p-3 label-mono">Pays</th>
                      <th className="text-right p-3 label-mono w-24">Vues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.top_countries.map((c, i) => (
                      <tr key={i} className="border-t border-black/10" data-testid={`country-row-${c.country}`}>
                        <td className="p-3 font-mono text-xs">{c.country}</td>
                        <td className="p-3">{c.country_name}</td>
                        <td className="p-3 text-right font-mono tabular-nums">{c.n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-4 text-[var(--muted)] italic text-sm">
                  Pas encore de données géographiques. Activez <code className="font-mono">GEOIP_ENABLED=true</code> et attendez les premiers visiteurs publics.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-2xl font-medium mb-4">Derniers événements (live)</h2>
        <div className="border border-black overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[var(--ink)] text-[var(--paper)]">
              <tr>
                <th className="text-left p-2 label-mono">Date</th>
                <th className="text-left p-2 label-mono">Chemin</th>
                <th className="text-left p-2 label-mono">Referrer</th>
                <th className="text-left p-2 label-mono">Pays</th>
                <th className="text-left p-2 label-mono">Session</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((e, i) => (
                <tr key={i} className="border-b border-black/10">
                  <td className="p-2 font-mono whitespace-nowrap">{e.ts?.slice(0, 19)}</td>
                  <td className="p-2 font-mono break-all">{e.path}</td>
                  <td className="p-2 font-mono break-all">{e.referrer_host || "—"}</td>
                  <td className="p-2 font-mono">{e.geo?.country || "—"}{e.geo?.city ? ` · ${e.geo.city}` : ""}</td>
                  <td className="p-2 font-mono">{e.session_id?.slice(0, 8)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-[var(--paper)] p-6" data-testid={testid}>
      <div className="label-mono text-[var(--muted)]">{label}</div>
      <div className="font-serif text-4xl mt-2 font-medium tabular-nums">{value}</div>
    </div>
  );
}
